<template>
  <div>
    <div class="your-details-section">
      <h2 class="your-details-heading">
        {{ t('donationGuide.paymentDeliveryDonationDetails.yourDetails') }}
      </h2>
      <v-row class="personal-info-fields">
        <v-col cols="12" sm="6" class="py-0">
          <option-label :title="t('donorDetails.form.firstname.label') + '*'" />
          <v-text-field
            v-model="donor.firstname"
            :placeholder="t('donorDetails.form.firstname.placeholder')"
            persistent-placeholder
            data-cy="donor-form-firstname-field"
            :rules="[rules.requiredField(t('donorDetails.form.firstname.label'))]"
            clearable
          />
        </v-col>
        <v-col cols="12" sm="6" class="py-0">
          <option-label :title="t('donorDetails.form.lastname.label') + '*'" />
          <v-text-field
            v-model="donor.surname"
            :placeholder="t('donorDetails.form.lastname.placeholder')"
            persistent-placeholder
            data-cy="donor-form-surname-field"
            :rules="[rules.requiredField(t('donorDetails.form.lastname.label'))]"
            clearable
          />
        </v-col>
      </v-row>
      <option-label :title="t('donorDetails.form.email.label')" />
      <v-text-field
        ref="email"
        v-model.trim="donorEmail"
        :placeholder="t('donorDetails.form.email.placeholder')"
        persistent-placeholder
        :rules="[rules.email]"
        :validate-on-blur="true"
        data-cy="donor-form-email-field"
        type="email"
        clearable
        @keydown.space.prevent
      />
      <v-slide-y-transition>
        <div v-if="isInvoicePayment">
          <v-row class="personal-info-fields">
            <v-col cols="12" sm="6" class="py-0">
              <option-label :title="t('donorDetails.form.address.label') + '*'" />
              <v-text-field
                v-model="donor.address.address1"
                :hint="t('donorDetails.form.address.hint')"
                :placeholder="t('donorDetails.form.address.placeholder')"
                persistent-placeholder
                data-cy="donor-form-address1-field"
                :rules="[rules.requiredField(t('donorDetails.form.address.label'))]"
                clearable
              />
            </v-col>
            <v-col cols="12" sm="6" class="py-0">
              <option-label :title="t('donorDetails.form.postcode.label') + '*'" />
              <v-text-field
                v-model="donor.address.postcode"
                :hint="t('donorDetails.form.postcode.hint')"
                :placeholder="t('donorDetails.form.postcode.placeholder')"
                persistent-placeholder
                data-cy="donor-form-postcode-field"
                :rules="[rules.requiredField(t('donorDetails.form.postcode.label'))]"
                clearable
              />
            </v-col>
          </v-row>
          <v-row class="personal-info-fields">
            <v-col cols="12" sm="6" class="py-0">
              <option-label :title="t('donorDetails.form.city.label') + '*'" />
              <v-text-field
                v-model="donor.address.city"
                :hint="t('donorDetails.form.city.hint')"
                :placeholder="t('donorDetails.form.city.placeholder')"
                persistent-placeholder
                data-cy="donor-form-city-field"
                :rules="[rules.requiredField(t('donorDetails.form.city.label'))]"
                clearable
              />
            </v-col>
            <v-col cols="12" sm="6" class="py-0">
              <option-label :title="t('donorDetails.form.country.label')" />
              <v-text-field
                v-model="donor.address.country"
                :hint="t('donorDetails.form.country.hint')"
                :placeholder="t('donorDetails.form.country.placeholder')"
                persistent-placeholder
                data-cy="donor-form-country-field"
                clearable
              />
            </v-col>
          </v-row>
        </div>
      </v-slide-y-transition>
    </div>

    <div class="personal-info-container pa-5 rounded-lg">
      <slot name="taxHeader" />
      <v-alert
        v-if="showError && showPersonalIdentityNumberField && isSsnAllowedCountry"
        border="start"
        variant="outlined"
        :close-text="t('generic.labels.close')"
        colored-border
        elevation="2"
        type="warning"
      >
        {{ t('donorDetails.pidRetrievalError') }}
      </v-alert>
      <v-slide-x-transition>
        <div v-if="showPersonalIdentityNumberField && isSsnAllowedCountry">
          <option-label :title="t('donorDetails.form.pid.label2') + '*'" />
          <v-text-field
            ref="ssn"
            v-model="donor.socialSecurityNumber"
            v-mask="ssnMask"
            :disabled="ssnLoading"
            :hint="t('donorDetails.form.pid.hint')"
            :loading="ssnLoading"
            persistent-placeholder
            :placeholder="t('donorDetails.form.pid.placeholder')"
            data-cy="donor-form-ssn-field"
            :rules="personalIdentityFieldRules"
            validate-on-input
            clearable
            :class="{ 'ssn-valid': ssnValid }"
          />
        </div>
      </v-slide-x-transition>
      <v-slide-y-transition>
        <div v-if="showSparConfirmation">
          <v-row class="personal-info-fields">
            <v-col cols="12" sm="6" class="py-0">
              <option-label :title="t('donorDetails.form.firstname.label')" />
              <v-text-field
                :model-value="sparConfirmation.firstname"
                persistent-placeholder
                data-cy="donor-form-spar-firstname-field"
                disabled
                readonly
              />
            </v-col>
            <v-col cols="12" sm="6" class="py-0">
              <option-label :title="t('donorDetails.form.lastname.label')" />
              <v-text-field
                :model-value="sparConfirmation.surname"
                persistent-placeholder
                data-cy="donor-form-spar-surname-field"
                disabled
                readonly
              />
            </v-col>
          </v-row>
          <v-row class="personal-info-fields">
            <v-col cols="12" sm="6" class="py-0">
              <option-label :title="t('donorDetails.form.address.label')" />
              <v-text-field
                :model-value="sparConfirmation.address.address1"
                persistent-placeholder
                data-cy="donor-form-spar-address1-field"
                disabled
                readonly
              />
            </v-col>
            <v-col cols="12" sm="6" class="py-0">
              <option-label :title="t('donorDetails.form.postcode.label')" />
              <v-text-field
                :model-value="sparConfirmation.address.postcode"
                persistent-placeholder
                data-cy="donor-form-spar-postcode-field"
                disabled
                readonly
              />
            </v-col>
          </v-row>
          <v-row class="personal-info-fields">
            <v-col cols="12" sm="6" class="py-0">
              <option-label :title="t('donorDetails.form.city.label')" />
              <v-text-field
                :model-value="sparConfirmation.address.city"
                persistent-placeholder
                data-cy="donor-form-spar-city-field"
                disabled
                readonly
              />
            </v-col>
            <v-col cols="12" sm="6" class="py-0">
              <option-label :title="t('donorDetails.form.country.label')" />
              <v-text-field
                :model-value="sparConfirmation.address.country"
                persistent-placeholder
                data-cy="donor-form-spar-country-field"
                disabled
                readonly
              />
            </v-col>
          </v-row>
        </div>
      </v-slide-y-transition>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue';
import store from '@/store';
import { ssnPattern } from '@/validators';
import { Donor } from '@/interfaces/donation/donor';
import { mask } from 'vue-the-mask';
import { getFromSpar } from '@/api/mainApi';
import OptionLabel from '../OptionLabel.vue';
import matomo from '@/services/matomoService';
import { isInvoice, PaymentMethod } from '@/classes/PaymentMethod';
import { useValidationRules } from '@/composables/useValidationRules';
import { useLabel } from '@/composables/useLabel';

export default defineComponent({
  components: {
    OptionLabel,
  },
  directives: {
    mask,
  },
  props: {
    value: {
      type: Object as () => Donor,
      default: () => ({}) as Donor,
    },
    error: {
      type: Boolean,
      default: false,
    },
    showPersonalIdentityNumberField: {
      type: Boolean,
      default: false,
    },
    paymentMethod: {
      type: Object as () => PaymentMethod,
      default: null,
    },
  },
  emits: ['update:value', 'update:error'],
  setup(props, { emit }) {
    const ssnLoading = ref(false);
    const ssnMask = ref('########-####');
    const ssnValid = ref(false);
    const allowedSsnCountries = ref(['SWEDEN', 'NORWAY']);
    const createEmptyAddress = () => ({
      address1: '',
      address2: '',
      address3: '',
      city: '',
      country: '',
      postcode: '',
    });
    const sparConfirmation = ref({
      firstname: '',
      surname: '',
      address: createEmptyAddress(),
    });

    const { rules } = useValidationRules();
    const { t } = useLabel();

    const isSsnAllowedCountry = computed((): boolean => {
      return allowedSsnCountries.value
        .map((c) => c.toUpperCase())
        .includes(userCountry.value.toUpperCase());
    });
    const userCountry = computed((): string => {
      return store.state.userLocation ?? 'SWEDEN';
    });
    const socialSecurityNumber = computed(() => {
      return donor.value.socialSecurityNumber;
    });
    const isInvoicePayment = computed((): boolean => {
      return !!props.paymentMethod && isInvoice(props.paymentMethod.type);
    });
    const showSparConfirmation = computed((): boolean => {
      return props.showPersonalIdentityNumberField && ssnValid.value;
    });
    // Set once a SPAR lookup has filled the customer fields, so a manually entered name is never wiped.
    let sparAutofilled = false;
    const clearSparAutofill = () => {
      if (sparAutofilled && donor.value) {
        donor.value.firstname = '';
        donor.value.surname = '';
        donor.value.address = createEmptyAddress();
      }
      sparAutofilled = false;
    };
    const resetSparConfirmation = () => {
      clearSparAutofill();
      sparConfirmation.value = {
        firstname: '',
        surname: '',
        address: createEmptyAddress(),
      };
    };
    const personalIdentityFieldRules = computed(() => {
      if (props.showPersonalIdentityNumberField && isSsnAllowedCountry.value) {
        return [
          (v: string) =>
            !!v ||
            t('validation.requiredField', { property: t('donorDetails.form.pid.label2') }) ||
            '',
          rules.value.pid,
        ];
      }
      return [rules.value.pid];
    });
    const donor = computed({
      get(): Donor {
        return props.value as Donor;
      },
      set(val: Donor) {
        emit('update:value', val);
      },
    });
    const showError = computed({
      get(): boolean {
        return props.error;
      },
      set(val: boolean) {
        emit('update:error', val);
      },
    });
    const donorEmail = computed({
      get(): string {
        return donor.value.email;
      },
      set(newValue: string) {
        donor.value.email = newValue;
      },
    });
    const clearTaxLookup = () => {
      ssnValid.value = false;
      showError.value = false;
      resetSparConfirmation();
      if (donor.value) {
        donor.value.socialSecurityNumber = '';
        donor.value.token = '';
      }
    };

    watch(
      () => props.showPersonalIdentityNumberField,
      (enabled) => {
        if (!enabled) {
          clearTaxLookup();
        }
      }
    );

    watch(socialSecurityNumber, async (newValue, oldValue) => {
      if (!allowedSsnCountries.value.includes(userCountry.value)) return;

      // Handle clearing the field
      if (!newValue || newValue.trim() === '') {
        ssnValid.value = false;
        showError.value = false;
        resetSparConfirmation();
        donor.value.token = '';
        return;
      }

      const newValueExclHypen = newValue.replace(/-/g, '');
      const oldValueExclHypen = (oldValue || '').replace(/-/g, '');
      if (newValueExclHypen !== oldValueExclHypen || showError.value) {
        showError.value = false;
        ssnValid.value = false; // Reset validation state
        resetSparConfirmation();
        donor.value.token = '';
        if (ssnPattern.test(newValue)) {
          ssnLoading.value = true;
          const formattedSsn: string = newValue.replace('-', '');
          // The SSN field is disabled while loading, but unticking the tax option still clears it.
          const isStale = () =>
            (donor.value.socialSecurityNumber || '').replace(/-/g, '') !== newValueExclHypen;
          getFromSpar(formattedSsn)
            .then((sparData) => {
              if (isStale()) return;
              ssnValid.value = true;
              // Non-agent sessions get masked values plus a token; on save the backend swaps in the
              // unmasked SPAR donor for that token. External agents get unmasked data and no token.
              donor.value.token = sparData.token || '';
              sparConfirmation.value = {
                firstname: (sparData.givenName || sparData.firstname || '').trim(),
                surname: (sparData.surname || '').trim(),
                address: {
                  ...createEmptyAddress(),
                  ...(sparData.address || {}),
                },
              };
              // Populate the customer fields above so they can be used directly (e.g. for invoices).
              // email, mobileNumber and invoiceEmail are not SPAR data, so they are left untouched.
              // Blank SPAR values never overwrite what the user has already entered.
              if (sparConfirmation.value.firstname) {
                donor.value.firstname = sparConfirmation.value.firstname;
              }
              if (sparConfirmation.value.surname) {
                donor.value.surname = sparConfirmation.value.surname;
              }
              const sparAddress = Object.fromEntries(
                Object.entries(sparConfirmation.value.address).filter(
                  ([, value]) => typeof value === 'string' && value.trim() !== ''
                )
              );
              donor.value.address = {
                ...createEmptyAddress(),
                ...(donor.value.address || {}),
                ...sparAddress,
              };
              sparAutofilled = true;
            })
            .catch(() => {
              if (isStale()) return;
              ssnValid.value = false; // Ensure invalid state on API failure
              resetSparConfirmation();
              donor.value.token = '';
              showError.value = true;
            })
            .finally(() => {
              matomo.trackEvent('donation_details', 'ssn_entered', 'donations_details_ssn_entered');
              ssnLoading.value = false;
            });
        } else {
          ssnValid.value = false;
          resetSparConfirmation();
          donor.value.token = '';
          showError.value = true;
        }
      }
    });

    return {
      ssnLoading,
      ssnMask,
      ssnValid,
      allowedSsnCountries,
      rules,
      t,
      isSsnAllowedCountry,
      userCountry,
      socialSecurityNumber,
      isInvoicePayment,
      showSparConfirmation,
      sparConfirmation,
      personalIdentityFieldRules,
      donor,
      showError,
      donorEmail,
    };
  },
});
</script>

<style scoped>
:deep(.ssn-valid .v-field) {
  background-color: #f3faf7 !important;
  border-color: #057a55 !important;
  color: #057a55 !important;
  border-radius: 8px !important;
}

:deep(.ssn-valid .v-input__control) {
  border-color: #057a55 !important;
}

:deep(.ssn-valid .v-field--focused .v-field__outline) {
  border-color: #057a55 !important;
}

:deep(.ssn-valid .v-field__outline__start),
:deep(.ssn-valid .v-field__outline__end) {
  border-color: #057a55 !important;
}
</style>
<style lang="scss" scoped>
.personal-info-fields {
  margin-left: -12px;
  margin-right: -12px;
}

.your-details-section {
  margin-bottom: 24px;
}

.your-details-heading {
  font-family: 'Lato', sans-serif;
  font-weight: 800;
  font-size: 20px;
  line-height: 140%;
  color: #6b7280;
  margin: 0 0 16px;
}

.personal-info-container {
  border: 1px dashed #047481;
  background-color: #f5f8f7;
  border-radius: 16px !important;
}
</style>
